/**
 * Onboarding welcome step (APP-002 §7).
 *
 * A polished welcome screen that communicates the product value briefly: what
 * the product evaluates, that the primary customer API requires an API key,
 * that the playground allows testing, that usage is metered, and that the
 * dashboard manages access and activity. No deep implementation terminology.
 */
import { BrandMark } from '@/components/navigation/BrandMark';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldCheck, Key, Sparkles, Gauge } from '@/components/icons';

const VALUE_POINTS = [
  {
    icon: ShieldCheck,
    title: 'Evaluate signup risk',
    body: 'Score signups before fake, disposable, or suspicious accounts enter your product.',
  },
  {
    icon: Key,
    title: 'API-key access',
    body: 'Send analysis requests from your application using a reveal-once API key.',
  },
  {
    icon: Sparkles,
    title: 'Test in the playground',
    body: 'Try the API under your real plan and quota without wiring a request manually.',
  },
  {
    icon: Gauge,
    title: 'Metered by plan',
    body: 'Usage is counted against your monthly quota and managed from this dashboard.',
  },
];

export function WelcomeStep() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <BrandMark className="h-12 w-12" />
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold tracking-tight">Welcome to Signup Risk</h2>
          <p className="mx-auto max-w-prose text-sm text-muted-foreground">
            Signup Risk helps you evaluate signups before fake, disposable, or suspicious
            accounts enter your product. Set up your account and start integrating in a few minutes.
          </p>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {VALUE_POINTS.map((point) => (
          <Card key={point.title} className="border-border/60">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <point.icon className="h-4 w-4" />
              </div>
              <div className="space-y-0.5 text-left">
                <p className="text-sm font-medium">{point.title}</p>
                <p className="text-xs text-muted-foreground">{point.body}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
